import { supabase } from "@/integrations/supabase/client";
import type {
  RealtimeChannel,
  RealtimePostgresChangesFilter,
  RealtimePostgresChangesPayload,
} from "@supabase/supabase-js";

type PostgresPayload = RealtimePostgresChangesPayload<Record<string, unknown>>;
type PostgresCallback = (payload: PostgresPayload) => void;
type PostgresEvent = "*" | "INSERT" | "UPDATE" | "DELETE";
type PostgresBinding = Omit<RealtimePostgresChangesFilter<"*">, "event"> & { event: PostgresEvent };
type AnyPostgresFilter = RealtimePostgresChangesFilter<"*">;

type HubEntry = {
  channel: RealtimeChannel | null;
  handlers: Set<PostgresCallback>;
  bindings: PostgresBinding[];
};

const postgresHubs = new Map<string, HubEntry>();

function hubTopic(channelName: string): string {
  return channelName.startsWith("realtime:") ? channelName : `realtime:${channelName}`;
}

function createHubFanOut(channelName: string, entry: HubEntry): PostgresCallback {
  return (payload) => {
    for (const h of entry.handlers) {
      try {
        h(payload);
      } catch (err) {
        console.error(`postgres hub ${channelName}:`, err);
      }
    }
  };
}

function isChannelRegistered(channel: RealtimeChannel | null): boolean {
  if (!channel) return false;
  return supabase.getChannels().includes(channel);
}

function ensureHubChannel(
  channelName: string,
  bindings: PostgresBinding[],
  fanOut: (payload: PostgresPayload) => void,
): RealtimeChannel {
  const topic = hubTopic(channelName);
  for (const existing of supabase.getChannels()) {
    if (existing.topic === topic) {
      supabase.removeChannel(existing);
    }
  }

  let channel = supabase.channel(channelName);
  for (const binding of bindings) {
    channel = channel.on("postgres_changes", binding as AnyPostgresFilter, fanOut);
  }
  return channel.subscribe();
}

/** Remove canal existente com o mesmo nome (evita erro após subscribe no HMR/StrictMode). */
export function removeSupabaseChannelByName(channelName: string): void {
  const hubEntry = postgresHubs.get(channelName);
  if (hubEntry?.channel) {
    supabase.removeChannel(hubEntry.channel);
    hubEntry.channel = null;
  }

  const topic = hubTopic(channelName);
  for (const channel of supabase.getChannels()) {
    if (channel.topic === topic) {
      supabase.removeChannel(channel);
    }
  }
}

/**
 * Canal compartilhado por nome: vários componentes podem escutar o mesmo tópico
 * sem colidir (ex.: useLeadTags montado em Conversations + LeadTagSelector).
 */
export function subscribePostgresHub(
  channelName: string,
  bindings: PostgresBinding | PostgresBinding[],
  handler: PostgresCallback,
): () => void {
  const bindingList = Array.isArray(bindings) ? bindings : [bindings];
  let entry = postgresHubs.get(channelName);
  if (!entry) {
    entry = { channel: null, handlers: new Set(), bindings: bindingList };
    postgresHubs.set(channelName, entry);
  }

  entry.handlers.add(handler);

  if (!entry.channel || !isChannelRegistered(entry.channel)) {
    entry.channel = ensureHubChannel(
      channelName,
      entry.bindings,
      createHubFanOut(channelName, entry),
    );
  }

  return () => {
    entry!.handlers.delete(handler);
    if (entry!.handlers.size === 0) {
      removeSupabaseChannelByName(channelName);
      postgresHubs.delete(channelName);
    }
  };
}

export function subscribePostgresChannel(
  channelName: string,
  binding: PostgresBinding,
  callback: PostgresCallback,
): RealtimeChannel {
  removeSupabaseChannelByName(channelName);
  return supabase
    .channel(channelName)
    .on("postgres_changes", binding as AnyPostgresFilter, callback)
    .subscribe();
}

/** Vários bindings no mesmo canal — todos .on() antes de subscribe(). */
export function subscribePostgresChannelMulti(
  channelName: string,
  listeners: Array<{ binding: PostgresBinding; callback: PostgresCallback }>,
  onStatus?: (status: string) => void,
): RealtimeChannel {
  removeSupabaseChannelByName(channelName);
  let channel = supabase.channel(channelName);
  for (const { binding, callback } of listeners) {
    channel = channel.on("postgres_changes", binding as AnyPostgresFilter, callback);
  }
  return onStatus ? channel.subscribe(onStatus) : channel.subscribe();
}

type ResilientOptions = {
  /** Chamado toda vez que o canal entra em SUBSCRIBED (inclusive após reconexão). */
  onSubscribed?: (isReconnect: boolean) => void;
  /** Chamado a cada mudança de status bruta. */
  onStatus?: (status: string) => void;
};

export type ResilientChannelHandle = {
  /** Reassina imediatamente (watchdog). Ignora se já estiver saudável. */
  ensureHealthy: () => void;
  isHealthy: () => boolean;
  unsubscribe: () => void;
};

const RESILIENT_MIN_BACKOFF_MS = 1000;
const RESILIENT_MAX_BACKOFF_MS = 30000;

/**
 * Canal postgres_changes com reassinatura automática (backoff + jitter).
 * Evita o estado "canal morto para sempre" após CHANNEL_ERROR/TIMED_OUT/CLOSED.
 */
export function subscribePostgresChannelResilient(
  channelName: string,
  listeners: Array<{ binding: PostgresBinding; callback: PostgresCallback }>,
  options: ResilientOptions = {},
): ResilientChannelHandle {
  let channel: RealtimeChannel | null = null;
  let backoffMs = RESILIENT_MIN_BACKOFF_MS;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;
  let hasConnectedOnce = false;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const teardown = () => {
    if (channel) {
      supabase.removeChannel(channel);
      channel = null;
    }
  };

  const connect = () => {
    if (disposed || channel) return;

    let next = supabase.channel(channelName);
    for (const { binding, callback } of listeners) {
      next = next.on("postgres_changes", binding as AnyPostgresFilter, callback);
    }

    channel = next.subscribe((status) => {
      if (disposed) return;
      options.onStatus?.(status);

      if (status === "SUBSCRIBED") {
        backoffMs = RESILIENT_MIN_BACKOFF_MS;
        clearTimer();
        const isReconnect = hasConnectedOnce;
        hasConnectedOnce = true;
        options.onSubscribed?.(isReconnect);
        return;
      }

      if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
        scheduleReconnect();
      }
    });
  };

  const scheduleReconnect = (immediate = false) => {
    if (disposed || timer) return;
    const jitter = Math.floor(Math.random() * 300);
    const delay = immediate ? 0 : backoffMs + jitter;
    backoffMs = Math.min(backoffMs * 2, RESILIENT_MAX_BACKOFF_MS);
    timer = setTimeout(() => {
      timer = null;
      teardown();
      connect();
    }, delay);
  };

  removeSupabaseChannelByName(channelName);
  connect();

  const isHealthy = () => Boolean(channel && channel.state === "joined");

  return {
    isHealthy,
    ensureHealthy: () => {
      if (disposed || isHealthy() || timer) return;
      scheduleReconnect(true);
    },
    unsubscribe: () => {
      disposed = true;
      clearTimer();
      teardown();
      removeSupabaseChannelByName(channelName);
    },
  };
}

